import KineticHero from "../components/KineticHero";
import { Reveal, ImageReveal } from "../components/motion/Reveal";
import EditorialMarquee from "../components/EditorialMarquee";
import { IMG, ROOMS, GALLERY } from "../lib/site";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const chapters = [
  {
    n: "01",
    title: "Home beyond the Hotel.",
    body: "The Haveli belongs to the Bohara Braj Bhushanjee family — the same family that gave Bundi State four Prime Ministers all in the 19th century. Every corner is lived-in, every antique inherited.",
  },
  {
    n: "02",
    title: "Painted by centuries.",
    body: "Courtyards adorned with Bundi School Frasco and miniature paintings — Ragmala, Radha-Krishna, hunting scenes — in the region's famous blues, greens, turquoise and gold.",
  },
  {
    n: "03",
    title: "Below the Palace.",
    body: "Set below the Garh Palace on the road up the hill. The rooftop dining terrace faces the fort — extraordinary at night, when the palace is lit and the town falls quiet.",
  },
];

export default function Home() {
  return (
    <div>
      <KineticHero />

      {/* Manifesto */}
      <section className="relative bg-bg py-28 md:py-40" data-testid="manifesto">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <div className="overline text-terracotta">The Haveli, in three chapters</div>
              <h2 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl mt-6 leading-[0.92] tracking-[-0.02em] text-ink">
                Where the past<br/><span className="italic text-terracotta">still lives on.</span>
              </h2>
              <p className="mt-8 text-ink-soft leading-relaxed max-w-sm">
                This late 17th century Haveli situated just below the Palace and Fort — its carved sandstone arches and painted verandahs opening onto a garden that has never left our family.
              </p>
              <Link to="/about" className="mt-8 inline-flex items-center gap-2 text-terracotta text-sm font-semibold group" data-testid="manifesto-cta">
                <span className="underline underline-offset-4 decoration-1">Read our story</span>
                <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"/>
              </Link>
            </Reveal>
          </div>

          <div className="md:col-span-7 md:col-start-6 space-y-14 md:space-y-20">
            {chapters.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className="grid grid-cols-12 gap-6 md:gap-8 items-start pb-14 md:pb-20 border-b border-[#E5E1D8] last:border-0">
                  <div className="col-span-3 md:col-span-2">
                    <div className="font-display text-6xl md:text-7xl text-[#E5E1D8] leading-none select-none">{c.n}</div>
                  </div>
                  <div className="col-span-9 md:col-span-10">
                    <h3 className="font-display text-2xl md:text-3xl leading-tight text-ink">{c.title}</h3>
                    <p className="mt-4 text-ink-soft leading-relaxed max-w-lg">{c.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deliberate spotlight photography */}
      <section className="relative bg-navy text-bg py-24 md:py-32 overflow-hidden grain" data-testid="spotlight">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
          <div className="md:col-span-7">
            <Reveal>
              <div className="relative">
                <div className="arch-mask border border-bg/10">
                  <ImageReveal src={IMG.terraceView} alt="Rooftop view of Bundi Palace" className="aspect-[4/5]"/>
                </div>
                <div className="absolute -bottom-6 -right-6 md:-bottom-10 md:-right-10 w-40 md:w-64 border border-bg/15 bg-navy">
                  <ImageReveal src={IMG.interior1} alt="Interior detail" className="aspect-square"/>
                </div>
              </div>
            </Reveal>
          </div>
          <div className="md:col-span-5">
            <Reveal delay={0.15}>
              <div className="overline text-gold">Illuminated view of Palace</div>
              <h2 className="font-display font-bold text-5xl md:text-6xl lg:text-7xl mt-5 leading-[0.92] text-bg drop-shadow-[0_2px_16px_rgba(0,0,0,0.55)]">
                A splendid view,<br/>after dark.
              </h2>
              <p className="mt-6 text-bg/85 leading-relaxed max-w-md">
                Haveli Braj Bhushanjee offers excellent view of Bundi Palace, Fort and Town —
                especially at night, when the palace is illuminated.
              </p>
              <Link to="/heritage-hotel" className="btn-pill mt-8 border border-bg/60 text-bg hover:bg-bg hover:text-navy" data-testid="spotlight-cta">
                Rooms &amp; dining
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Room preview */}
      <section className="bg-bg py-24 md:py-32" data-testid="room-preview">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
              <div>
                <div className="overline text-terracotta">Three ways to stay</div>
                <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-[0.95] text-ink">The rooms.</h2>
              </div>
              <p className="text-ink-soft max-w-sm text-sm md:text-base leading-relaxed">
                Eighteen rooms in all — each one renovated to keep the old architecture intact, several with original Bundi School murals on the walls.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6">
            {ROOMS.map((r, i) => (
              <Reveal key={r.id} delay={i * 0.08}>
                <Link to="/heritage-hotel#booking" className="group block hover-lift" data-testid={`room-card-${r.id}`}>
                  <div className="overflow-hidden border border-[#E5E1D8] bg-surface">
                    <ImageReveal src={r.image} alt={r.name} className="aspect-[4/5]"/>
                  </div>
                  <div className="pt-5 flex items-start justify-between gap-4">
                    <div>
                      <div className="overline text-terracotta">{r.subtitle}</div>
                      <h3 className="font-display font-bold text-2xl mt-2 text-ink">{r.name}</h3>
                      <div className="text-xs text-ink-soft mt-1">{r.size}</div>
                    </div>
                    <div className="text-right">
                      <div className="overline text-ink-soft">From</div>
                      <div className="font-display font-bold text-2xl text-ink mt-1">₹{r.price.toLocaleString("en-IN")}</div>
                      <div className="text-[11px] text-ink-soft">/ night</div>
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <EditorialMarquee />

      {/* Gallery preview */}
      <section className="bg-bg py-24 md:py-32" data-testid="gallery-preview">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
              <div>
                <div className="overline text-terracotta">Gallery</div>
                <h2 className="font-display font-bold text-4xl md:text-5xl lg:text-6xl mt-4 leading-[0.95] text-ink">Every surface, painted.</h2>
              </div>
              <Link to="/gallery" className="btn-pill btn-ghost self-start md:self-auto" data-testid="gallery-preview-cta">
                See the full gallery
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-6 md:grid-cols-12 gap-3 md:gap-4">
            {GALLERY.slice(0, 6).map((src, i) => {
              const spans = ["col-span-6 md:col-span-8 aspect-[16/10]", "col-span-6 md:col-span-4 aspect-[4/5]", "col-span-3 md:col-span-4 aspect-square", "col-span-3 md:col-span-4 aspect-square", "col-span-6 md:col-span-4 aspect-[4/5]", "col-span-6 md:col-span-12 aspect-[21/9]"];
              return (
                <Reveal key={i} delay={i * 0.05} className={spans[i]}>
                  <div className="w-full h-full overflow-hidden border border-[#E5E1D8]">
                    <ImageReveal src={src} alt={`Haveli gallery ${i+1}`} className="w-full h-full"/>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-terracotta text-bg py-24 md:py-32 relative overflow-hidden grain" data-testid="final-cta">
        <div className="mx-auto max-w-[1440px] px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <div className="overline">Reserve direct — best rate</div>
            <h2 className="font-display font-bold text-5xl md:text-7xl lg:text-8xl mt-4 leading-[0.9] text-bg drop-shadow-[0_2px_20px_rgba(0,0,0,0.35)]">
              Come stay<br/>with the family.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link to="/heritage-hotel#booking" className="btn-pill bg-bg text-terracotta hover:bg-ink hover:text-bg" data-testid="final-cta-btn">
              Enquire now <ArrowUpRight size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
